import {
  Card,
  Title,
  Box,
  CardProps,
  ActionIcon,
  useMantineTheme,
  Group,
  Image,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { FC, memo, ReactNode } from "react";
import { IoMdSave } from "react-icons/io";
import {
  MdOutlineMiscellaneousServices,
  MdDelete,
  MdUndo,
} from "react-icons/md";
import { RiPencilFill } from "react-icons/ri";

export interface FormCardProps extends CardProps {
  title: string;
  titleContent?: ReactNode;
  loading?: boolean;
  isFormActive?: boolean;
  startImage?: string;
  hideCard?: boolean;
  size?: "sm" | "md" | "lg";

  onEdit?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  onHeaderClick?: () => void;

  bannerContent?: string;
  bannerColor?: string;
  bannerBgColor?: string;
  overflow?: "visible" | "hidden";
  disabled?: boolean;
}

const FormCard: FC<FormCardProps> = ({
  title,
  titleContent,
  loading = false,
  isFormActive = true,
  hideCard = false,

  onEdit,
  onSave,
  onDelete,
  onCancel,
  onHeaderClick,

  bannerContent = "",
  bannerBgColor = "#4d4d4d",
  bannerColor = "#fff",
  children,
  startImage,
  size = "lg",
  overflow = "hidden",
  disabled,
  ...restProps
}) => {
  const theme = useMantineTheme();
  const modals = useModals();

  const openDeleteModal = () =>
    onDelete &&
    modals.openContextModal("delete", {
      closeOnClickOutside: false,
      title: "Confirm Delete",
      centered: true,
      innerProps: {
        onDelete,
      },
    });

  let border = `1px solid ${theme.colors.primary[6]}`;
  let bg = theme.colors.primary[6];
  let color = theme.colors.primaryTextColor[6];

  let actionIconSx = {
    backgroundColor: theme.colors.primary[6],
    "&:hover": {
      backgroundColor: theme.colors.primary[0],
    },
  };

  if (hideCard) return <form onSubmit={onSave}>{children}</form>;
  return (
    <form onSubmit={onSave}>
      <Card
        style={{
          border,
          color,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflow: overflow, // added for date picker cutoff issue
        }}
        radius="md"
        w="100%"
        p={0}
        bg="transparent"
        {...restProps}
      >
        <Group
          bg={bg}
          p="0 15px"
          position="apart"
          style={{
            borderRadius: overflow === "visible" ? "8px 8px 0 0" : 0,
            borderBottom: children ? border : "none",
            color,
            height: size === "lg" ? 44 : size === "md" ? 40 : 30,
          }}
          onClick={onHeaderClick}
        >
          <Group>
            {startImage &&
              (startImage !== "NA" ? (
                <Image
                  display={"flex"}
                  style={{
                    alignItems: "center",
                    width: 30,
                  }}
                  h={30}
                  w={30}
                  maw={30}
                  radius={"sm"}
                  mx="auto"
                  src={startImage}
                  alt={`${title} image`}
                />
              ) : (
                <MdOutlineMiscellaneousServices size={30} />
              ))}
            {titleContent || (
              <Title
                order={size === "lg" ? 4 : size === "md" ? 5 : 5}
                weight={size === "lg" ? 700 : 500}
              >
                {title}
              </Title>
            )}
          </Group>
          <Group
            position="apart"
            //align="center"
            spacing={10}
            onClick={(e) => e.stopPropagation()}
          >
            {disabled ? (
              <div></div>
            ) : isFormActive ? (
              <>
                {onEdit && (
                  <ActionIcon onClick={onEdit} sx={actionIconSx}>
                    <RiPencilFill color={color} size={23} />
                  </ActionIcon>
                )}
                {onDelete && (
                  <ActionIcon
                    loading={loading}
                    onClick={openDeleteModal}
                    variant="transparent"
                    color="red"
                    sx={actionIconSx}
                  >
                    <MdDelete size={23} />
                  </ActionIcon>
                )}
              </>
            ) : (
              <>
                {onCancel && (
                  <ActionIcon onClick={onCancel} sx={actionIconSx}>
                    <MdUndo color={color} size={23} />
                  </ActionIcon>
                )}
                <ActionIcon
                  type="submit"
                  loading={loading}
                  sx={actionIconSx}
                  styles={{
                    root: {
                      outline: "none",
                      border: "none",
                    },
                  }}
                >
                  <IoMdSave color={color} size={23} />
                </ActionIcon>
              </>
            )}
          </Group>
        </Group>
        {bannerContent && (
          <Title
            order={6}
            bg={bannerBgColor}
            color={bannerColor}
            p="2px 0"
            ta="center"
          >
            {bannerContent}
          </Title>
        )}
        {children && (
          <Box
            p={size === "lg" ? 15 : 10}
            style={{ backgroundColor: theme.colors.secondary[6] }}
          >
            {children}
          </Box>
        )}
      </Card>
    </form>
  );
};

export default memo(FormCard);
